'use client';
// src/pages/parent/ParentOnboarding.jsx
//
// Post-invite welcome wizard for new parents. Closes the "scavenger hunt"
// gap: previously a parent who accepted an invite had to find Settings →
// Profile → Phone Number → verify, then Settings → Notifications → enable
// push, all on their own. This wizard walks them through it in one flow
// right after they accept.
//
// Flow:
//   AcceptInvite (parent) → success → /parent/welcome → 4 steps → /parent/teams
//
// Skip-friendly: every step except Welcome has a "Skip for now" link.
// Skipping still marks parentOnboardingComplete so we don't re-show the
// wizard. They can complete the missing pieces from Settings later.
//
// Idempotent: if a parent has already completed (or skipped) onboarding,
// they're bounced straight to /parent/teams. Reaching /parent/welcome
// directly via URL is harmless.

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  doc, setDoc, updateDoc, serverTimestamp, onSnapshot,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app, db } from '@/firebase/firebase';
import { useAuth } from '@/context/AuthContext';
import {
  CheckCircleIcon,
  ArrowRightIcon,
  ArrowPathIcon,
  DevicePhoneMobileIcon,
  BellAlertIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import {
  SMS_CONSENT_TEXT,
  SMS_CONSENT_VERSION,
  CONSENT_SOURCES,
} from '@/constants/consent';
import { LEGAL_URLS } from '@/constants/legal';

const HEAD_FONT = "'Barlow Condensed', sans-serif";
const TOTAL_STEPS = 4;

/* ── E.164 normalize (mirror of ProfileSettings) ─────────────────────── */
function normalizeToE164(raw) {
  if (!raw) return '';
  const t = String(raw).trim();
  if (t.startsWith('+')) {
    const d = t.slice(1).replace(/\D/g, '');
    return d.length >= 10 && d.length <= 15 ? `+${d}` : '';
  }
  const d = t.replace(/\D/g, '');
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d.startsWith('1')) return `+${d}`;
  return '';
}
function fmtPhoneDisplay(e164) {
  if (!e164) return '';
  if (e164.startsWith('+1') && e164.length === 12) {
    const d = e164.slice(2);
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  }
  return e164;
}

/* ── Detect Capacitor environment for push registration ──────────────── */
function isCapacitor() {
  return typeof window !== 'undefined'
    && (window.Capacitor?.isNativePlatform?.() === true
        || window.Capacitor?.getPlatform?.() === 'ios'
        || window.Capacitor?.getPlatform?.() === 'android');
}

/* ── Reusable atoms ──────────────────────────────────────────────────── */
function ProgressBar({ step, total }) {
  return (
    <div className="flex items-center gap-1.5 mb-6" aria-label={`Step ${step} of ${total}`}>
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          className={clsx(
            'h-1 flex-1 rounded-full transition-colors',
            i < step ? 'bg-[#b5f03d]' : 'bg-zinc-200'
          )}
        />
      ))}
    </div>
  );
}

function Card({ children }) {
  return (
    <div className="bg-white rounded-2xl border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] p-6 sm:p-8">
      {children}
    </div>
  );
}

function PrimaryButton({ children, ...props }) {
  return (
    <button
      type="button"
      className={clsx(
        'inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] px-5 py-3 text-sm font-bold uppercase tracking-wide text-[#0d1f14] shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97]',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#b5f03d]',
        props.className
      )}
      style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
      {...props}
    >
      {children}
    </button>
  );
}

function SkipLink({ children, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="block mx-auto mt-4 text-xs font-semibold text-zinc-400 hover:text-zinc-600 underline-offset-2 hover:underline disabled:opacity-50"
    >
      {children}
    </button>
  );
}

/* ── Main wizard ─────────────────────────────────────────────────────── */
export default function ParentOnboarding() {
  const navigate = useNavigate();
  const { firebaseUser, userProfile } = useAuth();
  const [step, setStep] = useState(1);

  // Phone verify state
  const [phoneInput, setPhoneInput]   = useState('');
  const [codeInput, setCodeInput]     = useState('');
  const [phoneStep, setPhoneStep]     = useState('idle'); // 'idle' | 'sending' | 'awaitingCode' | 'verifying'

  // Push opt-in state
  const [enablingPush, setEnablingPush] = useState(false);
  const [pushOk, setPushOk]             = useState(false);

  // First-team name for the welcome message — listen on user profile so
  // we don't have to wait on TeamContext.
  const [firstTeamName, setFirstTeamName] = useState(null);

  /* ── Already-completed gate: bounce to /parent/teams if onboarding
       has been finished or explicitly skipped before. */
  useEffect(() => {
    if (!firebaseUser?.uid) return;
    const ref = doc(db, 'users', firebaseUser.uid);
    return onSnapshot(ref, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data() || {};
      if (data.parentOnboardingComplete) {
        navigate('/parent/teams', { replace: true });
      }
    });
  }, [firebaseUser?.uid, navigate]);

  /* ── Pull the first team name for the welcome step (best-effort) ── */
  useEffect(() => {
    if (!firebaseUser?.uid) return;
    let cancelled = false;
    (async () => {
      try {
        const { collection, query, where, limit, getDocs } = await import('firebase/firestore');
        const snap = await getDocs(query(
          collection(db, 'teams'),
          where('parentIds', 'array-contains', firebaseUser.uid),
          limit(1),
        ));
        if (cancelled || snap.empty) return;
        setFirstTeamName(snap.docs[0].data()?.name || null);
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [firebaseUser?.uid]);

  const greetingName = useMemo(() => {
    const dn = userProfile?.displayName || firebaseUser?.displayName || '';
    return dn ? dn.split(' ')[0] : '';
  }, [userProfile?.displayName, firebaseUser?.displayName]);

  /* ── Persist completion / skip ──────────────────────────────────── */
  const markComplete = async () => {
    if (!firebaseUser?.uid) return;
    try {
      await updateDoc(doc(db, 'users', firebaseUser.uid), {
        parentOnboardingComplete: true,
        parentOnboardingCompletedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.warn('[ParentOnboarding] mark complete failed', err);
    }
  };

  const finish = async () => {
    await markComplete();
    navigate('/parent/teams', { replace: true });
  };

  /* ── Phone verify handlers (mirror of ProfileSettings flow) ──── */
  const handleSendCode = async () => {
    const phone = normalizeToE164(phoneInput);
    if (!phone) {
      toast.error('Enter a valid phone number');
      return;
    }
    setPhoneStep('sending');
    const tid = toast.loading('Sending code…');
    try {
      const fn = httpsCallable(getFunctions(app, 'us-central1'), 'startPhoneVerification');
      await fn({ phoneNumber: phone });
      setPhoneStep('awaitingCode');
      toast.success(`Code sent to ${fmtPhoneDisplay(phone)}`, { id: tid });
    } catch (err) {
      toast.error(err?.message || 'Failed to send code', { id: tid });
      setPhoneStep('idle');
    }
  };

  const handleVerifyCode = async () => {
    const phone = normalizeToE164(phoneInput);
    const code  = codeInput.trim();
    if (!phone || !code) {
      toast.error('Enter the 6-digit code');
      return;
    }
    setPhoneStep('verifying');
    const tid = toast.loading('Verifying…');
    try {
      const verify = httpsCallable(getFunctions(app, 'us-central1'), 'checkPhoneVerification');
      const { data } = await verify({ phoneNumber: phone, code });
      if (!data?.success) {
        toast.error('Invalid code — try again', { id: tid });
        setPhoneStep('awaitingCode');
        return;
      }

      // Persist phone + auto-enable Master SMS (verify is the opt-in event)
      await Promise.all([
        updateDoc(doc(db, 'users', firebaseUser.uid), {
          phoneNumber: phone,
          phoneVerified: true,
          'trust.phoneVerified': true,
          updatedAt: serverTimestamp(),
        }),
        setDoc(
          doc(db, `users/${firebaseUser.uid}/notificationSettings/default`),
          { smsOptIn: true, updatedAt: serverTimestamp() },
          { merge: true }
        ),
      ]);

      // Audit record (non-blocking) — same shape as ProfileSettings
      try {
        const recordConsent = httpsCallable(getFunctions(app, 'us-central1'), 'recordConsent');
        await recordConsent({
          type: 'sms',
          action: 'granted',
          phoneNumber: phone,
          source: CONSENT_SOURCES.PROFILE_VERIFY,
          consentTextVersion: SMS_CONSENT_VERSION,
          consentText: SMS_CONSENT_TEXT,
          verificationChannel: 'twilio_verify',
          identitySnapshot: {
            displayName: userProfile?.displayName || firebaseUser?.displayName || null,
            email: firebaseUser?.email || null,
          },
        });
      } catch (logErr) {
        console.warn('[ParentOnboarding] consent log failed', logErr);
      }

      toast.success('Phone verified · SMS active', { id: tid });
      setStep(3); // advance to push step
    } catch (err) {
      toast.error(err?.message || 'Verification failed', { id: tid });
      setPhoneStep('awaitingCode');
    }
  };

  /* ── Push opt-in handler ───────────────────────────────────────── */
  const handleEnablePush = async () => {
    setEnablingPush(true);
    try {
      // Capacitor native: use the @capacitor/push-notifications plugin
      if (isCapacitor()) {
        const pkg = '@capacitor/push-notifications';
        const mod = await import(/* @vite-ignore */ pkg).catch(() => null);
        if (!mod?.PushNotifications) {
          toast.error('Push not available on this device');
          setEnablingPush(false);
          return;
        }
        const { PushNotifications } = mod;
        let perm = await PushNotifications.checkPermissions();
        if (perm.receive !== 'granted') {
          perm = await PushNotifications.requestPermissions();
        }
        if (perm.receive === 'granted') {
          await PushNotifications.register();
          setPushOk(true);
          toast.success('Notifications on');
        } else {
          toast('Permission declined — you can enable later in Settings', { icon: 'ℹ️' });
        }
      } else {
        // Web path: requires (a) browser support for the Notification API
        // and (b) the parent be linked to at least one team to bind the
        // FCM token. Onboarding is the place parents typically hit this
        // BEFORE either condition is met (iOS Safari can't do push at
        // all unless installed as a PWA; brand-new parents often haven't
        // accepted any invite yet). Both cases used to silently no-op
        // and leave the button looking broken — we now toast clearly.

        // 1. Browser must support the Notification API at all.
        if (typeof window === 'undefined' || typeof window.Notification === 'undefined') {
          toast('Push isn\'t supported in this browser. On iOS, install Scrimly to your Home Screen first.', {
            icon: 'ℹ️',
            duration: 6500,
          });
          return;
        }

        const { registerTokenForTeam } = await import('@/firebase/messaging');
        const { collection, query, where, limit, getDocs } = await import('firebase/firestore');
        const teamSnap = await getDocs(query(
          collection(db, 'teams'),
          where('parentIds', 'array-contains', firebaseUser.uid),
          limit(1),
        ));
        const teamId = teamSnap.empty ? null : teamSnap.docs[0].id;

        if (teamId) {
          // Full register-token-for-team path.
          const r = await registerTokenForTeam(teamId, firebaseUser.uid);
          if (r.ok) {
            setPushOk(true);
            toast.success('Notifications on');
          } else if (r.reason === 'denied') {
            toast('Permission declined — enable later in browser settings', { icon: 'ℹ️' });
          } else if (r.reason === 'unsupported') {
            toast('Push not supported in this browser', { icon: 'ℹ️' });
          } else {
            toast.error('Could not enable push');
          }
        } else {
          // No team yet — request browser permission anyway. Once the
          // parent later accepts a team invite, the team-join flow
          // registers the FCM token using this granted permission.
          let perm = window.Notification.permission;
          if (perm === 'default') {
            try {
              perm = await window.Notification.requestPermission();
            } catch (err) {
              console.warn('[ParentOnboarding] requestPermission failed', err);
            }
          }
          if (perm === 'granted') {
            setPushOk(true);
            toast.success('Notifications enabled. They\'ll start arriving once you\'re linked to a team.');
          } else if (perm === 'denied') {
            toast('Permission declined — enable later in your browser settings.', {
              icon: 'ℹ️',
              duration: 6500,
            });
          } else {
            // Permission still 'default' — user dismissed the OS prompt
            // without picking. Tell them what just happened.
            toast('Notification permission wasn\'t granted. Tap the button again to retry.', {
              icon: 'ℹ️',
              duration: 6500,
            });
          }
        }
      }
    } catch (err) {
      console.warn('[ParentOnboarding] push enable failed', err);
      toast.error('Could not enable push');
    } finally {
      setEnablingPush(false);
    }
  };

  /* ── Render per step ───────────────────────────────────────────── */
  return (
    <div
      className="min-h-screen bg-[#f7faf5] flex items-center justify-center p-4"
      style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
    >
      <div className="w-full max-w-md">
        <ProgressBar step={step} total={TOTAL_STEPS} />

        {step === 1 && (
          <Card>
            <div className="text-center">
              <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
                <SparklesIcon className="h-8 w-8 text-[#5a8a1f]" />
              </div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#5a8a1f]"
                 style={{ fontFamily: HEAD_FONT }}>
                Welcome
              </p>
              <h1 className="mt-2 text-2xl sm:text-3xl font-bold uppercase tracking-tight text-[#0d1f14]"
                  style={{ fontFamily: HEAD_FONT }}>
                {greetingName ? `You're in, ${greetingName}!` : "You're in!"}
              </h1>
              <p className="mt-3 text-sm text-zinc-600 leading-relaxed">
                {firstTeamName ? (
                  <>You've joined <strong className="text-[#0d1f14]">{firstTeamName}</strong>. Let's take 30 seconds to make sure you don't miss anything from the coach.</>
                ) : (
                  <>Let's take 30 seconds to make sure you don't miss anything from your child's coach.</>
                )}
              </p>
            </div>
            <div className="mt-7">
              <PrimaryButton onClick={() => setStep(2)}>
                Get started
                <ArrowRightIcon className="h-4 w-4" />
              </PrimaryButton>
              <SkipLink onClick={finish}>Skip for now</SkipLink>
            </div>
          </Card>
        )}

        {step === 2 && (
          <Card>
            <div className="text-center mb-5">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
                <DevicePhoneMobileIcon className="h-7 w-7 text-[#5a8a1f]" />
              </div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#5a8a1f]"
                 style={{ fontFamily: HEAD_FONT }}>
                Step 2 · Phone
              </p>
              <h2 className="mt-2 text-xl sm:text-2xl font-bold uppercase tracking-tight text-[#0d1f14]"
                  style={{ fontFamily: HEAD_FONT }}>
                Get game-day texts
              </h2>
              <p className="mt-2 text-sm text-zinc-600">
                Verify your phone for last-minute changes (rain-out, time change). Cancel any time.
              </p>
            </div>

            {phoneStep !== 'awaitingCode' && phoneStep !== 'verifying' ? (
              <div className="space-y-3">
                <input
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={phoneInput}
                  onChange={(e) => setPhoneInput(e.target.value)}
                  placeholder="(415) 555-1234"
                  disabled={phoneStep === 'sending'}
                  className="block w-full rounded-xl border border-zinc-200 bg-white px-3.5 py-3 text-sm text-[#0d1f14] placeholder:text-zinc-400 focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30"
                />
                <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-3 text-[11px] text-zinc-600 leading-relaxed">
                  {SMS_CONSENT_TEXT}
                </div>
                <p className="text-[11px] text-zinc-500">
                  See full{' '}
                  <a href={LEGAL_URLS.SMS} target="_blank" rel="noopener noreferrer"
                     className="font-semibold text-[#5a8a1f] hover:text-[#3d6010] underline">SMS Policy</a>
                  {' '}and{' '}
                  <a href={LEGAL_URLS.PRIVACY} target="_blank" rel="noopener noreferrer"
                     className="font-semibold text-[#5a8a1f] hover:text-[#3d6010] underline">Privacy Policy</a>.
                </p>
                <PrimaryButton
                  onClick={handleSendCode}
                  disabled={phoneStep === 'sending' || !phoneInput.trim()}
                >
                  {phoneStep === 'sending' ? (
                    <><ArrowPathIcon className="animate-spin h-4 w-4" /> On it…</>
                  ) : (
                    <>Send code <ArrowRightIcon className="h-4 w-4" /></>
                  )}
                </PrimaryButton>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-xs text-zinc-600 text-center">
                  Code sent to <strong className="text-[#0d1f14]">{fmtPhoneDisplay(normalizeToE164(phoneInput))}</strong>
                </p>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  maxLength={6}
                  className="block w-full rounded-xl border border-[#b5f03d]/40 bg-white px-3.5 py-3 text-center text-2xl font-mono tracking-widest text-[#0d1f14] focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30"
                />
                <PrimaryButton
                  onClick={handleVerifyCode}
                  disabled={phoneStep === 'verifying' || codeInput.length < 6}
                >
                  {phoneStep === 'verifying' ? (
                    <><ArrowPathIcon className="animate-spin h-4 w-4" /> Verifying…</>
                  ) : (
                    <>Verify <ArrowRightIcon className="h-4 w-4" /></>
                  )}
                </PrimaryButton>
                <button
                  type="button"
                  onClick={() => { setPhoneStep('idle'); setCodeInput(''); }}
                  className="block mx-auto text-xs font-semibold text-zinc-500 hover:text-zinc-700"
                >
                  Use a different number
                </button>
              </div>
            )}

            <SkipLink onClick={() => setStep(3)} disabled={phoneStep === 'verifying'}>
              Skip for now
            </SkipLink>
          </Card>
        )}

        {step === 3 && (
          <Card>
            <div className="text-center mb-5">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
                <BellAlertIcon className="h-7 w-7 text-[#5a8a1f]" />
              </div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#5a8a1f]"
                 style={{ fontFamily: HEAD_FONT }}>
                Step 3 · Notifications
              </p>
              <h2 className="mt-2 text-xl sm:text-2xl font-bold uppercase tracking-tight text-[#0d1f14]"
                  style={{ fontFamily: HEAD_FONT }}>
                Turn on notifications
              </h2>
              <p className="mt-2 text-sm text-zinc-600">
                Get instant alerts on your {isCapacitor() ? 'phone' : 'browser'} when the coach posts a schedule change or a new message.
              </p>
            </div>

            {pushOk ? (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-center">
                <CheckCircleIcon className="mx-auto h-6 w-6 text-emerald-600 mb-1" />
                <p className="text-sm font-semibold text-emerald-800">Notifications enabled</p>
              </div>
            ) : (
              <PrimaryButton onClick={handleEnablePush} disabled={enablingPush}>
                {enablingPush ? (
                  <><ArrowPathIcon className="animate-spin h-4 w-4" /> Enabling…</>
                ) : (
                  <>Enable notifications <BellAlertIcon className="h-4 w-4" /></>
                )}
              </PrimaryButton>
            )}

            {pushOk && (
              <PrimaryButton className="mt-3" onClick={() => setStep(4)}>
                Continue
                <ArrowRightIcon className="h-4 w-4" />
              </PrimaryButton>
            )}

            <SkipLink onClick={() => setStep(4)} disabled={enablingPush}>
              {pushOk ? '' : 'Skip for now'}
            </SkipLink>
          </Card>
        )}

        {step === 4 && (
          <Card>
            <div className="text-center">
              <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-100 ring-1 ring-emerald-200">
                <CheckCircleIcon className="h-9 w-9 text-emerald-600" />
              </div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#5a8a1f]"
                 style={{ fontFamily: HEAD_FONT }}>
                All set
              </p>
              <h2 className="mt-2 text-2xl sm:text-3xl font-bold uppercase tracking-tight text-[#0d1f14]"
                  style={{ fontFamily: HEAD_FONT }}>
                You're ready
              </h2>
              <p className="mt-3 text-sm text-zinc-600 leading-relaxed">
                {firstTeamName ? (
                  <>Your <strong className="text-[#0d1f14]">{firstTeamName}</strong> dashboard is set up. Tap below to see what's coming up this week.</>
                ) : (
                  <>Tap below to see your team and what's coming up this week.</>
                )}
              </p>
            </div>
            <div className="mt-7">
              <PrimaryButton onClick={finish}>
                Go to my dashboard
                <ArrowRightIcon className="h-4 w-4" />
              </PrimaryButton>
            </div>
          </Card>
        )}

        <p className="mt-6 text-center text-[11px] text-zinc-400">
          You can change any of these later in Settings.
        </p>
      </div>
    </div>
  );
}
